import React from 'react';
import {
    BedDouble, BookOpen, Bot, CalendarDays, CircleDollarSign, ClipboardList, Gift, Lock, MessageCircle, Package, Phone, Plus, Receipt,
    Send, ShieldCheck, Clock, Sparkles, Star, UsersRound, Wallet, Wrench, Zap, ShoppingBag, BarChart3, FileText, Check, Layers,
} from 'lucide-react';
import type { ModuleCatalogue } from '../../lib/organisationApi';
import { estModulePayant, libelleTarifModule } from '../../lib/abonnementsApi';
import { Pastille } from './EtatsPage';

/**
 * Carte-module (maquette L01-05 / L01-06) : tuile arrondie ; module activé = carte posée avec pastille « Activé » ;
 * non activé = emplacement en pointillés avec « + » ; verrouillé = grisé avec cadenas et raison écrite.
 * Activation : la carte s'incline d'environ 8° puis se pose (420 ms) ; désactivation : 250 ms ;
 * `prefers-reduced-motion` : simple fondu (règle CSS `.carte-module` dans index.css).
 */

const ICONES: Record<string, React.ComponentType<{ size?: number }>> = {
    livre_reservations: BookOpen, liste_attente: ClipboardList, exception_service: Star, anti_no_show: ShieldCheck, chambres: BedDouble,
    planning: CalendarDays, menage_qr: Sparkles, maintenance: Wrench, commande: ShoppingBag, services_extras: Package,
    crm: UsersRound, fidelite: Gift, avis: Star, campagnes: Send, facture: Receipt, fournisseurs: Package, tresorerie: Wallet,
    tableaux_bord: BarChart3, agent_telephone: Phone, agent_whatsapp: MessageCircle, automatisations: Zap, police_taxe_sejour: FileText,
    rgpd_audit: ShieldCheck, prestations: CircleDollarSign, avis_ia: Bot,
};

export const iconeModule = (nom: string) => ICONES[nom] || Layers;

interface Props {
    module: ModuleCatalogue;
    /** Vrai pendant l'instant où la carte vient d'être posée (inclinaison de départ). */
    incline?: boolean;
    interactif: boolean;
    /** Vrai seulement pour le super-admin : le client voit l'état de ses modules, il ne les active pas lui-même. */
    peutBasculer: boolean;
    /** Vrai pour la direction d'un client : elle peut demander l'activation d'un module à L'IAmani. */
    peutDemander?: boolean;
    onClick: () => void;
}

export const CarteModule: React.FC<Props> = ({ module: m, incline = false, interactif, peutBasculer, peutDemander = false, onClick }) => {
    const actif = m.etat === 'actif';
    const verrouille = m.etat === 'verrouille';
    const Icone = iconeModule(m.name);
    const verrouBase = actif && m.core; // module de base : actif, non désactivable
    const demandeEnCours = !actif && m.demande === 'en_attente';
    const demandable = !actif && !verrouille && !peutBasculer && peutDemander && !demandeEnCours;
    const label = m.label + (demandeEnCours ? ', demande d\'activation envoyée, en attente de réponse' : demandable ? ', non activé. Appuyer pour demander l\'activation' : verrouille ? `, non disponible : ${m.raison}` : actif ? (verrouBase ? ', activé, module de base' : peutBasculer ? ', activé. Appuyer pour désactiver' : ', activé') : peutBasculer ? ', non activé. Appuyer pour activer' : ', non activé');

    return (
        <button
            type="button"
            onClick={onClick}
            disabled={verrouille || verrouBase || (peutBasculer ? !interactif : !demandable)}
            aria-label={label}
            data-module={m.name}
            data-etat={m.etat}
            className={[
                'carte-module relative text-left rounded-[14px] border p-3.5 min-h-[132px] flex flex-col gap-2 w-full',
                actif ? 'bg-surface border-border border-solid' : verrouille ? 'bg-surface-soft border-border border-dashed cursor-not-allowed' : 'bg-transparent border-border border-dashed hover:border-content',
                !verrouille && !verrouBase && ((interactif && peutBasculer) || demandable) ? 'cursor-pointer' : '',
            ].join(' ')}
            style={{
                borderWidth: actif ? 1 : 1.5,
                transform: incline ? 'rotate(-8deg) translateY(-8px)' : 'none',
                transition: incline ? 'none' : 'transform 420ms cubic-bezier(.2,.7,.2,1), opacity 250ms, background 250ms, border-color 250ms',
            }}
        >
            <span className="flex items-start gap-2.5">
                <span className="w-8 h-8 rounded-[10px] flex items-center justify-center shrink-0" style={actif ? { background: 'var(--acc-bg)', color: 'var(--acc-fg)' } : { background: 'var(--bg-soft)', color: 'var(--text-muted)' }}>
                    <Icone size={16} />
                </span>
                <span className="text-[13.5px] font-semibold text-content leading-tight pt-0.5 min-w-0 flex-1">{m.label}</span>
                {actif && m.a_terminer && (
                    <span aria-label="1 action requise" className="w-[18px] h-[18px] rounded-full text-[10.5px] font-bold flex items-center justify-center shrink-0" style={{ background: 'var(--acc)', color: 'var(--acc-on)' }}>1</span>
                )}
            </span>
            <span className="text-[12px] text-content-muted leading-snug">{m.description}</span>
            <span className="mt-auto flex flex-wrap items-center gap-1.5 text-[12px] font-semibold">
                {actif && (
                    <Pastille ton="ok" icone={<Check size={11} strokeWidth={3} />}>Activé</Pastille>
                )}
                {actif && m.transverse && (
                    <Pastille>Transverse</Pastille>
                )}
                {actif && m.a_terminer && <span className="text-state-warn">Terminer le paramétrage</span>}
                {!actif && !verrouille && peutBasculer && <span className="inline-flex items-center gap-1 text-content"><Plus size={13} /> Activer ce module</span>}
                {demandeEnCours && <Pastille ton="info" icone={<Clock size={11} />}>Demande envoyée</Pastille>}
                {demandable && <span className="inline-flex items-center gap-1 text-content"><Plus size={13} /> {m.demande === 'refusee' ? 'Redemander l\'activation' : 'Demander l\'activation'}</span>}
                {demandable && estModulePayant(m.tarif) && <span className="text-[11.5px] font-semibold text-content-muted">{libelleTarifModule(m.tarif)}</span>}
                {demandable && m.demande === 'refusee' && <span className="basis-full text-[11.5px] font-normal text-state-warn">Demande refusée{m.reponse_demande ? ` : ${m.reponse_demande}` : ''}</span>}
                {!actif && !verrouille && !peutBasculer && !peutDemander && <span className="inline-flex items-center gap-1 text-content-muted"><Lock size={12} /> Activation par L'IAmani</span>}
                {verrouille && <span className="inline-flex items-center gap-1 text-content-muted"><Lock size={12} /> {m.raison}</span>}
            </span>
        </button>
    );
};
